import React, {Fragment} from 'react';

import {AlertInfo, MainFooter, SentinelLoading, StyledGrid} from 'presentation/components';
import Tutorial from 'presentation/components/Tutorial';
import {AppointmentCard} from 'presentation/modules/myAppointments/ui/components/AppointmentCard';
import {AppointmentCardSkeleton} from 'presentation/modules/myAppointments/ui/components/AppointmentCardSkeleton';

import {translations} from 'presentation/translations-new';
import {Placement} from 'react-joyride';

import {AppointmentsFilterHeader} from './components/AppointmentsFilterHeader';
import {AppointmentsListError} from './components/AppointmentsListError';
import {AppointmentsListLoading} from './components/AppointmentsListLoading';
import {EmptyAppointmentsList} from './components/EmptyAppointmentsList';
import {useMyAppointmentsList} from './hooks/useMyAppointmentsList';
import {AppointmentsListContainer, StyledBox} from './styles';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {HandleMessage} from 'presentation/components/HandleMessage';

const {
	telehealthDocumentsAccess: {myAppointmentsAlert},
	tutorial: {step1, step3},
} = translations['pt-br'].privateFlow.myAppointments;

export function MyAppointmentsList() {
	const {
		appointmentsListFiltered,
		refresh,
		appointmentsListError,
		appointmentsListLoading,
		isEmpty,
		emptyStateDescription,
		shouldHideScheduleButton,
		shouldHideAlertInfo,
		isTablet,
		handleNewAppointment,
		list,
		scrollableContentRef,
		loadingNewPageState,
		handleVisible,
		listHasCardWithFavoriteButton,
		firstFavoriteCardIndex,
	} = useMyAppointmentsList();

	if (appointmentsListError) {
		return (
			<AppointmentsListError refresh={refresh} handleNewAppointment={handleNewAppointment} />
		);
	}

	if (appointmentsListLoading) {
		return <AppointmentsListLoading />;
	}

	if (isEmpty) {
		return (
			<EmptyAppointmentsList
				description={shouldHideScheduleButton ? '' : emptyStateDescription}
				hideButton={shouldHideScheduleButton}
				hideAlertInfo={shouldHideAlertInfo}
				handleNewAppointment={handleNewAppointment}
			/>
		);
	}

	return (
		<Fragment>
			<AppointmentsListContainer ref={scrollableContentRef}>
				{shouldHideAlertInfo ? null : (
					<AlertInfo.Root sx={{mb: 2}}>
						<AlertInfo.Description>{myAppointmentsAlert}</AlertInfo.Description>
					</AlertInfo.Root>
				)}
				<Fragment>
					<AppointmentsFilterHeader
						handleNewAppointment={handleNewAppointment}
						totalCount={appointmentsListFiltered.length}
					/>
					<StyledGrid.Root>
						{list?.map((appointment, index) => (
							<StyledGrid.Item key={appointment.bookingNumber} sm={12} md={12} lg={6}>
								<AppointmentCard
									appointment={appointment}
									hasFavoriteButton={
										appointment.bookingType === BookingTypeEnum.HEALTH ||
										appointment.bookingType === BookingTypeEnum.TELEHEALTH
									}
									hasTutorialLikeButtonId={index === firstFavoriteCardIndex}
								/>
							</StyledGrid.Item>
						))}

						{loadingNewPageState
							? Array.from({length: isTablet ? 1 : 2}).map((_, index) => (
									<StyledGrid.Item
										key={`skeleton${String(index)}`}
										sm={12}
										md={12}
										lg={6}>
										<AppointmentCardSkeleton />
									</StyledGrid.Item>
								))
							: null}

						<SentinelLoading
							sx={{
								visibility: 'hidden',
							}}
							onVisible={handleVisible}
							isLoading={loadingNewPageState}
						/>
					</StyledGrid.Root>
				</Fragment>
			</AppointmentsListContainer>

			<StyledBox>
				<MainFooter />
			</StyledBox>

			<Tutorial
				steps={[
					{
						target: '#myAppointmentsTutorial',
						content: step1,
						placement: 'bottom' as Placement,
						disableBeacon: true,
					},
					...(listHasCardWithFavoriteButton
						? [
								{
									target: '#seeHistoryDetailsLikeButton',
									content: <HandleMessage message={step3} />,
									placement: 'bottom' as Placement,
									disableBeacon: true,
								},
							]
						: []),
				]}
				storageKey="myAppointmentsTutorial"
			/>
		</Fragment>
	);
}
